import type { BookingStatus } from "design-components";
import {
  formatBookingArrivalLabel,
  BOOKING_EDIT_ALLOWED_STATUSES,
  isBookingEditAllowed,
  isBookingDetailActionsAllowed,
} from "design-components";
import type { BookingDeletePreviewData } from "api-client";
import type { BookingDeleteCarrierAssignment, BookingDeleteSummary } from "./booking-delete-drawer.types";

/** AC-AT8-007 — message body max length. */
export const BOOKING_DELETE_MESSAGE_MAX_LENGTH = 500;

/** Optional reason on delete (AV-103); max length. */
export const BOOKING_DELETE_REASON_MAX_LENGTH = 500;

/**
 * Bookings that cannot be deleted or edited from list/detail toolbars.
 * Extend when API adds explicit DELETED on `BookingRow`.
 */
export const BOOKING_ACTION_BLOCKED_STATUSES: readonly BookingStatus[] = [
  "IN_PROGRESS",
  "COMPLETE",
  "NO_SHOW",
  "DELETED",
];

export const BOOKING_DELETE_BLOCKED_STATUSES = BOOKING_ACTION_BLOCKED_STATUSES;

/** Bookings editable from the list toolbar (AV-44 v1 — No Show deferred). */
export { BOOKING_EDIT_ALLOWED_STATUSES, isBookingEditAllowed, isBookingDetailActionsAllowed };

export const BOOKING_EDIT_BLOCKED_STATUSES = BOOKING_ACTION_BLOCKED_STATUSES;

export function parseBookingDeleteRecipientEmails(raw: string): string[] {
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function isMultipieceEquipmentPieces(equipmentPieces: string): boolean {
  const normalized = equipmentPieces.trim().toLowerCase();
  return normalized.includes("multiple");
}

function mapPreviewCarrierAssignments(
  preview: BookingDeletePreviewData,
): BookingDeleteCarrierAssignment[] {
  if (preview.carrierAssignments.length > 0) {
    return preview.carrierAssignments.map((assignment) => ({
      carrierId: assignment.carrierId,
      name: assignment.carrierName,
      assignedQuantity: assignment.assignedQuantity,
      primaryContactName: assignment.primaryContactName,
      phone: assignment.primaryContactNumber,
      email: assignment.email,
    }));
  }
  if (preview.carrier) {
    return [
      {
        carrierId: preview.carrier.carrierId,
        name: preview.carrier.carrierName,
        assignedQuantity: null,
        primaryContactName: preview.carrier.primaryContactName,
        phone: preview.carrier.primaryContactNumber,
        email: preview.carrier.email,
      },
    ];
  }
  return [];
}

export function mapBookingDeletePreviewToDrawer(preview: BookingDeletePreviewData): {
  booking: BookingDeleteSummary;
  carrierAssignments: BookingDeleteCarrierAssignment[];
  canNotifyCarrier: boolean;
  containerQuantity: number | null;
  isMultipiece: boolean;
  initialRecipients: string[];
  initialMessageBody: string;
} {
  const scheduledAt = preview.scheduledDateTime.trim()
    ? formatBookingArrivalLabel(preview.scheduledDateTime)
    : "";
  const legacyArrival = [preview.arrivalDate, preview.arrivalTime].filter(Boolean).join(" ").trim();
  const displayWhen = scheduledAt || legacyArrival;
  const carrierAssignments = mapPreviewCarrierAssignments(preview);
  const isMultipiece =
    isMultipieceEquipmentPieces(preview.equipmentPieces) || carrierAssignments.length > 1;

  return {
    booking: {
      bookingId: String(preview.bookingId),
      bookingNumber: preview.bookingNumber,
      arrivalAt: displayWhen || "—",
      locationLabel: preview.locationName,
    },
    carrierAssignments,
    canNotifyCarrier: preview.canNotifyCarrier,
    containerQuantity: preview.containerQuantity,
    isMultipiece,
    initialRecipients: parseBookingDeleteRecipientEmails(preview.defaultRecipientEmails),
    initialMessageBody: preview.defaultEmailMessage,
  };
}

/** FR-AV186-DP-004 — quantity column for delete-preview carrier rows. */
export function formatDeletePreviewCarrierQuantity(
  assignedQuantity: number | null,
  options: { containerQuantity?: number | null; isMultipiece: boolean },
): string {
  if (assignedQuantity != null) {
    return String(assignedQuantity);
  }
  if (!options.isMultipiece) {
    if (options.containerQuantity != null && options.containerQuantity > 0) {
      return String(options.containerQuantity);
    }
    return "1";
  }
  return "—";
}

export function buildBookingDeleteDefaultMessage(summary: BookingDeleteSummary): string {
  const when = formatBookingDeleteDateTime(summary.arrivalAt);
  const loc = summary.locationLabel?.trim() || "—";
  return [
    `This message notifies you that booking ${summary.bookingNumber.trim() || "—"} has been cancelled by the operator.`,
    `Scheduled: ${when}.`,
    `Location: ${loc}.`,
    "",
    "If you have questions, reply to this thread or contact your operator representative.",
  ].join("\n");
}

export function formatBookingDeleteDateTime(arrivalAt: string): string {
  return formatBookingArrivalLabel(arrivalAt);
}

export function isBookingDeleteAllowed(status: BookingStatus): boolean {
  return !BOOKING_DELETE_BLOCKED_STATUSES.includes(status);
}

export function buildBookingDeleteSuccessDescription(
  bookingNumber: string,
  apiMessage: string | undefined,
  emailSent: boolean,
): string {
  if (bookingNumber && apiMessage) {
    return `Booking ${bookingNumber}: ${apiMessage}`;
  }
  if (bookingNumber) {
    return emailSent
      ? `Booking ${bookingNumber} was deleted and the carrier was notified.`
      : `Booking ${bookingNumber} was deleted successfully.`;
  }
  return (
    apiMessage ||
    (emailSent ? "Booking marked as deleted; carrier notified." : "Booking marked as deleted.")
  );
}
